import moment from 'moment'

import { getExperienceById } from 'api/experiences'
import { getAirportsSearch } from 'api/flights'
import { getOrderItemFlightDetails } from 'api/order'
import config from 'constants/config'
import {
  ISO_DATE_FORMAT,
  SHORT_TIME_FORMAT_24_HOUR,
} from 'constants/dateFormats'
import memoize from 'lib/memoize'
import offerPageURL, { getExperienceOfferPageURL } from 'lib/offerPageURL'
import { CreateTripItemRequest } from 'tripPlanner/api/tripItem/types'
import { Place } from 'tripPlanner/api/types'
import { BookmarkDatesAndOccupancy } from 'tripPlanner/types/tripItem'
import {
  concatStringsWithComma,
  convertBedbankPropertyAddressToRegion,
  convertOfferPropertyGeoDataToRegion,
  formatDmytoIsoDate,
} from 'tripPlanner/utils'

export type CreateTourRequestBody = Extract<
  CreateTripItemRequest,
  { type: 'TOUR' }
>

export type CreateCruiseRequestBody = Extract<
  CreateTripItemRequest,
  {
    type: 'CRUISE'
  }
>

export type CreateHotelRequestBody = Extract<
  CreateTripItemRequest,
  { type: 'HOTEL' }
>

export type CreateExperienceRequestBody = Extract<
  CreateTripItemRequest,
  { type: 'EXPERIENCE' }
>

export type CreateFlightRequestBody = Extract<
  CreateTripItemRequest,
  { type: 'FLIGHT' }
>

export const getPlaceFromOffer = (
  offer: App.Offer | App.OfferSummary,
): CreateHotelRequestBody['place'] => {
  if (offer.property) {
    const { name, latitude, longitude, geoData, address } = offer.property
    let region = ''
    if (geoData) {
      region = convertOfferPropertyGeoDataToRegion(geoData)
    }

    return {
      type: 'GEO_MULTI_DATES',
      id: geoData.placeId ?? undefined,
      name,
      address,
      point: {
        lat: latitude,
        lng: longitude,
      },
      region,
    }
  }

  return {
    type: 'CUSTOM_MULTI_DATES',
    name: offer.name,
    address: offer.location,
  }
}

interface ConvertOrderExperienceItemToExperienceTripItemParams {
  orderItem: App.OrderExperienceItem
  orderId: string
  currentCurrency: string
  currentRegionCode: string
  orderItemIds: string[]
}

export const convertOrderExperienceItemToExperienceTripItem = async ({
  orderItem,
  orderId,
  currentCurrency,
  currentRegionCode,
  orderItemIds,
}: ConvertOrderExperienceItemToExperienceTripItemParams): Promise<CreateExperienceRequestBody> => {
  const { experienceId } = orderItem

  const experience = await getExperienceById(experienceId, {
    currentCurrency,
    currentRegionCode,
  })

  // Will a user always be accessing their order from their original region when travelling?
  // The confirmationUrl will always contain a region code from where they purchased
  const regionCode = window.location.pathname.split('/')[1]
  const baseUrl = `${window.location.protocol}//${window.location.hostname}/${regionCode}`
  const confirmationUrl = baseUrl + `/booking/confirmation?orderId=${orderId}`

  // ToDo: Check if there is a better way of doing this.
  const offerUrl = baseUrl + getExperienceOfferPageURL(experience)

  // handle multi location experiences
  const redemptionLocation =
    experience.redemptionLocations?.find(
      (location) => location.id === orderItem.redemptionLocation?.id,
    ) ?? experience.location

  const payload: CreateExperienceRequestBody = {
    type: 'EXPERIENCE',
    name: experience.name,
    sourceType: 'LE_Experience',
    isBooked: true,
    startDate: orderItem.date,
    startTime: orderItem.time,
    endDate: orderItem.date,
    place: {
      type: 'GEO_MULTI_DATES',
      name: redemptionLocation.name,
      region: redemptionLocation.name,
      point: {
        lat: redemptionLocation.latitude,
        lng: redemptionLocation.longitude,
      },
      photoUrl: experience.images[0]?.url,
    },
    confirmationCode: orderId,
    confirmationItemCodes: orderItemIds,
    confirmationUrl,
    price: experience.price,
    url: offerUrl,
    code: experience.id,
  }

  return payload
}

function formatBedbankCheckInOutTime(timeString: string): string {
  return moment(timeString, 'h:mm a').format(SHORT_TIME_FORMAT_24_HOUR)
}

export const convertOrderBedbankItemToHotelTripItem = (
  bedbankItem: App.OrderBedbankItem,
): CreateHotelRequestBody => {
  const {
    orderId,
    checkIn: startDate,
    checkOut: endDate,
    offer,
    total: price,
    roomTypeId,
  } = bedbankItem
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { property, name } = offer

  // Will a user always be accessing their order from their original region when travelling?
  // The confirmationUrl will always contain a the region code from where they purchased
  const regionCode = window.location.pathname.split('/')[1]
  const baseUrl = `${window.location.protocol}//${window.location.hostname}/${regionCode}`
  const confirmationUrl = baseUrl + `/booking/confirmation?orderId=${orderId}`
  const offerUrl = baseUrl + offerPageURL(offer)

  const address = `${property.address.line1}, ${property.address.city}, ${property.address.postalCode}, ${property.address.countryName}`
  const region = convertBedbankPropertyAddressToRegion(property.address)

  let checkInTime = startDate.format(SHORT_TIME_FORMAT_24_HOUR)
  let checkOutTime = endDate.format(SHORT_TIME_FORMAT_24_HOUR)

  if (checkInTime === '00:00' && offer.property.finePrint?.checkIn?.beginTime) {
    checkInTime = formatBedbankCheckInOutTime(
      offer.property.finePrint?.checkIn?.beginTime,
    )
  }

  if (checkOutTime === '00:00' && offer.property.finePrint?.checkOut?.time) {
    checkOutTime = formatBedbankCheckInOutTime(
      offer.property.finePrint?.checkOut?.time,
    )
  }

  const payload: CreateHotelRequestBody = {
    type: 'HOTEL',
    sourceType: 'LE_Hotel_Bedbank',
    isBooked: true,
    startTime: checkInTime === '00:00' ? undefined : checkInTime,
    startDate: startDate.format(ISO_DATE_FORMAT),
    endTime: checkOutTime === '00:00' ? undefined : checkOutTime,
    endDate: endDate.format(ISO_DATE_FORMAT),
    place: {
      type: 'GEO_MULTI_DATES',
      name,
      address,
      point: {
        lat: offer.property.latitude,
        lng: offer.property.longitude,
      },
      photoUrl: `${config.IMAGE_HOST}/q_auto:eco,c_fill,g_auto,w_520,ar_16:9/${offer.image.id}.jpeg`,
      region,
    },
    confirmationCode: orderId,
    confirmationItemCodes: [bedbankItem.itemId],
    confirmationUrl,
    price,
    url: offerUrl,
    code: offer.id,
    subCode: roomTypeId,
  }

  return payload
}

export type FlightWithAirports = {
  flight: App.Flight
  departureAirport: App.AirportLocation | undefined
  arrivalAirport: App.AirportLocation | undefined
}

export function formatAirportLocationToRegionString(
  airportLocation: App.AirportLocation | undefined,
) {
  return concatStringsWithComma(
    airportLocation?.cityDesc,
    airportLocation?.country,
  )
}

export const convertFlightItemToFlightTripItem = (
  flight: FlightWithAirports,
  orderItem: App.OrderFlightItem,
): CreateFlightRequestBody => {
  const {
    flight: {
      departingAirport,
      departingAirportName,
      arrivalAirport,
      arrivalAirportName,
      flightNumber,
      departingDate,
      departingTime,
      arrivalDate,
      arrivalTime,
      carrierName,
    },
    departureAirport,
    arrivalAirport: arrivalAirportObject,
  } = flight

  // Will a user always be accessing their order from their original region when travelling?
  // The confirmationUrl will always contain a the region code from where they purchased
  const regionCode = window.location.pathname.split('/')[1]
  const url = `${window.location.protocol}//${window.location.hostname}/${regionCode}`
  const orderUrl = `${url}/booking/confirmation?orderId=${orderItem.orderId}`

  const payload: CreateFlightRequestBody = {
    type: 'FLIGHT',
    sourceType: 'LE_Flight',
    originAirportCode: departingAirport,
    destinationAirportCode: arrivalAirport,
    flightNumber,
    startTime: departingTime,
    startDate: formatDmytoIsoDate(departingDate),
    endTime: arrivalTime,
    endDate: formatDmytoIsoDate(arrivalDate),
    startPlace: {
      type: 'GEO',
      name:
        departureAirport?.airportName ??
        departureAirport?.cityDesc ??
        departingAirport,
      address: departureAirport
        ? `${departureAirport.cityDesc}, ${departureAirport.country}`
        : departingAirportName,
      point: {
        lat: departureAirport?.lt ?? 0,
        lng: departureAirport?.lg ?? 0,
      },
      region: formatAirportLocationToRegionString(departureAirport),
    },
    endPlace: {
      type: 'GEO',
      name:
        arrivalAirportObject?.airportName ??
        arrivalAirportObject?.cityDesc ??
        arrivalAirport,
      address: arrivalAirportObject
        ? `${arrivalAirportObject.cityDesc}, ${arrivalAirportObject.country}`
        : arrivalAirportName,
      point: {
        lat: arrivalAirportObject?.lt ?? 0,
        lng: arrivalAirportObject?.lg ?? 0,
      },
      region: formatAirportLocationToRegionString(arrivalAirportObject),
    },
    isBooked: true,
    confirmationUrl: orderUrl,
    airlineName: carrierName,
    price: Number(orderItem.total),
    confirmationCode: orderItem.orderId,
    confirmationItemCodes: [orderItem.itemId],
  }

  return payload
}

export const getFlightsFromFlightOrder = async (
  orderItem: App.OrderFlightItem,
): Promise<FlightWithAirports[]> => {
  const { itemId, orderId } = orderItem

  const flightDetail = await getOrderItemFlightDetails({ itemId, orderId })

  const flights = [
    ...(flightDetail.journey.departing.flights ?? []),
    ...(flightDetail.journey.returning?.flights ?? []),
  ]

  const flightsWithAirports: FlightWithAirports[] = []

  for (let index = 0; index < flights.length; index++) {
    const flight = flights[index]
    const arrivalAirports = (
      await getAirportsSearch(flight.arrivalAirport)
    ).filter((airport) => airport.airportCode === flight.arrivalAirport)
    const departureAirports = (
      await getAirportsSearch(flight.departingAirport)
    ).filter((airport) => airport.airportCode === flight.departingAirport)

    flightsWithAirports.push({
      flight,
      departureAirport:
        departureAirports.length == 1 ? departureAirports[0] : undefined,
      arrivalAirport:
        arrivalAirports.length == 1 ? arrivalAirports[0] : undefined,
    })
  }

  return flightsWithAirports
}

export const formatDateForPayload = (
  dateMoment: '' | moment.Moment | undefined,
): string | undefined => {
  if (dateMoment && dateMoment.isValid()) {
    return dateMoment.format(ISO_DATE_FORMAT)
  }
}

export const formatTimeForPayload = (
  timeMoment: '' | moment.Moment | undefined,
): string | undefined => {
  if (timeMoment && timeMoment.isValid()) {
    return timeMoment.format(SHORT_TIME_FORMAT_24_HOUR)
  }
}

export function getLeHotelCheckInAndOutTimes(
  offer: App.Offer | App.OfferSummary | undefined,
) {
  if (!offer) return []
  let startTime: string | undefined
  let endTime: string | undefined

  if ('facilities' in offer) {
    const checkInIndex = offer.facilities.indexOf('Check-in: ')
    const checkOutIndex = offer.facilities.indexOf('Checkout: ')

    if (checkInIndex && checkOutIndex) {
      startTime = checkInIndex
        ? offer?.facilities.slice(checkInIndex)?.split(' ')?.at(1)
        : undefined

      endTime = checkOutIndex
        ? offer?.facilities.slice(checkOutIndex)?.split(' ')?.at(1)
        : undefined
    }
  }
  const startTimeMoment = startTime
    ? moment(startTime, ['H', 'ha', 'HH:mm', 'h:mm a'])
    : undefined
  const endTimeMoment = endTime
    ? moment(endTime, ['H', 'ha', 'HH:mm', 'h:mm a'])
    : undefined

  return [startTimeMoment, endTimeMoment]
}

export const convertStandardOrderItemToHotelTripItem = (
  order: App.Order,
  orderItem: App.OrderItem,
  offer: App.Offer | undefined,
  currentRegionCode: string,
  orderItemIds: string[],
): CreateHotelRequestBody => {
  const { reservation, offer: orderOffer } = orderItem
  const orderId = order.id

  const startDate = reservation?.startDate
  const endDate = reservation?.endDate

  const baseUrl = `${window.location.protocol}//${window.location.hostname}/${currentRegionCode}`
  const confirmationUrl = baseUrl + `/booking/confirmation?orderId=${orderId}`
  const offerUrl = baseUrl + offerPageURL(orderOffer)

  const imageId = offer ? offer.image.id : orderItem.offer.image.id
  let place: CreateHotelRequestBody['place']

  const startDateMoment = startDate && moment(startDate)
  const endDateMoment = endDate && moment(endDate)
  const [startTimeMoment, endTimeMoment] = getLeHotelCheckInAndOutTimes(offer)

  let region: string | undefined
  if (offer?.property?.geoData) {
    region = convertOfferPropertyGeoDataToRegion(offer.property.geoData)
  }

  if (offer?.property?.latitude && offer.property.longitude) {
    place = {
      type: 'GEO_MULTI_DATES',
      name: offer.property.name,
      address: offer?.property?.address ?? offer.location,
      point: {
        lat: offer.property.latitude,
        lng: offer.property.longitude,
      },
      photoUrl: `${config.IMAGE_HOST}/${imageId}.jpeg`,
      region,
    }
  } else {
    place = {
      type: 'CUSTOM_MULTI_DATES',
      name: orderOffer.name,
      address: offer?.property?.address ?? orderOffer.location,
      region,
    }
  }

  const payload: CreateHotelRequestBody = {
    type: 'HOTEL',
    sourceType: 'LE_Hotel',
    isBooked: true,
    startDate: formatDateForPayload(startDateMoment),
    startTime: formatTimeForPayload(startTimeMoment),
    endDate: formatDateForPayload(endDateMoment),
    endTime: formatTimeForPayload(endTimeMoment),
    place,
    phone: undefined,
    confirmationCode: orderId,
    confirmationItemCodes: orderItemIds,
    confirmationUrl,
    price: Number(orderItem.total),
    url: offerUrl,
    code: orderOffer.id,
    subCode:
      'roomTypeId' in orderItem.package
        ? orderItem.package.roomTypeId
        : undefined,
  }

  return payload
}

export const createTourV2Payload = (
  order: App.Order,
  orderItem: Tours.V2OrderItem,
  offer: Tours.V2Offer,
  currentRegionCode: string,
  orderItemIds: string[],
  snapshot?: Tours.V2OfferSnapshot,
): CreateTourRequestBody | CreateCruiseRequestBody => {
  const orderId = order.id
  const baseUrl = `${window.location.protocol}//${window.location.hostname}/${currentRegionCode}`
  const confirmationUrl = baseUrl + `/booking/confirmation?orderId=${orderId}`
  const offerUrl = baseUrl + offerPageURL(offer)
  const imageId = offer?.image?.id ?? snapshot?.imageId

  // ToDo: We don't have appropriate addresses here probably check with Tour's team on how to fetch the Region.
  const payload: CreateTourRequestBody = {
    type: 'TOUR',
    name: orderItem.tour.tourOptionTitle,
    isBooked: true,
    sourceType: 'LE_Tour',
    startDate: orderItem.tour.startDate,
    endDate: orderItem.tour.endDate,
    startPlace: {
      type: 'CUSTOM',
      name: orderItem.tour.startLocation,
      photoUrl: imageId
        ? `${config.IMAGE_HOST}/q_auto:eco,c_fill,g_auto,w_520,ar_16:9/${imageId}.jpeg`
        : undefined,
    },
    endPlace: {
      type: 'CUSTOM',
      name: orderItem.tour.endLocation,
      photoUrl: imageId
        ? `${config.IMAGE_HOST}/q_auto:eco,c_fill,g_auto,w_520,ar_16:9/${imageId}.jpeg`
        : undefined,
    },
    confirmationCode: order.id,
    confirmationItemCodes: orderItemIds,
    price: Number(orderItem.total),
    confirmationUrl,
    url: offerUrl,
    code: offer.id,
  }

  return payload
}

export const convertOrderItemToTourOrCruiseTripItem = (
  order: App.Order,
  orderItem: App.OrderItem,
  offer: App.Offer,
  currentRegionCode: string,
  orderItemIds: string[],
): CreateTourRequestBody | CreateCruiseRequestBody => {
  const { reservation, offer: orderOffer } = orderItem
  const orderId = order.id

  const startDate = reservation?.startDate
    ? moment(reservation.startDate).format(ISO_DATE_FORMAT)
    : undefined
  const endDate = reservation?.endDate
    ? moment(reservation.endDate).format(ISO_DATE_FORMAT)
    : undefined

  const baseUrl = `${window.location.protocol}//${window.location.hostname}/${currentRegionCode}`
  const confirmationUrl = baseUrl + `/booking/confirmation?orderId=${orderId}`
  const offerUrl = baseUrl + offerPageURL(orderOffer)

  const offerPackage = ('packages' in offer && offer?.packages[0]) ?? null
  const imageId = offer ? offer.image.id : orderItem.offer.image.id

  let startPlace: Place = {
    type: 'CUSTOM',
    name: offer.location,
    address: offer.property?.address ?? offer.locationHeading,
    photoUrl: imageId
      ? `${config.IMAGE_HOST}/q_auto:eco,c_fill,g_auto,w_520,ar_16:9/${imageId}.jpeg`
      : undefined,
  }

  let endPlace: Place = {
    type: 'CUSTOM',
    name: offer.location,
    address: offer.property?.address ?? offer.locationHeading,
    photoUrl: imageId
      ? `${config.IMAGE_HOST}/q_auto:eco,c_fill,g_auto,w_520,ar_16:9/${imageId}.jpeg`
      : undefined,
  }

  let timezone: string | undefined = undefined

  if (offerPackage && offerPackage?.tour) {
    let startPlaceRegion = ''
    if (offerPackage.tour.geoData) {
      startPlaceRegion = convertOfferPropertyGeoDataToRegion(
        offerPackage.tour.geoData,
      )
    }
    startPlace = {
      ...startPlace,
      type: 'GEO',
      point: {
        lat: offerPackage.tour.latitude,
        lng: offerPackage.tour.longitude,
      },
      region: startPlaceRegion,
    }

    let endPlaceRegion = ''
    if (offerPackage.tour.geoData) {
      endPlaceRegion = convertOfferPropertyGeoDataToRegion(
        offerPackage.tour.geoData,
      )
    }
    // ToDo: Confirm with Tour's team to make sure we have the correct start and end place.
    endPlace = {
      ...endPlace,
      type: 'GEO',
      point: {
        lat: offerPackage.tour.latitude,
        lng: offerPackage.tour.longitude,
      },
      region: endPlaceRegion,
    }

    timezone = offerPackage.tour.timezone
  }

  if (offer.holidayTypes.includes('Cruises')) {
    const payload: CreateCruiseRequestBody = {
      type: 'CRUISE',
      name: offer.name,
      isBooked: true,
      sourceType: 'LE_Cruise',
      startDate,
      startTimezone: timezone,
      endDate,
      endTimezone: timezone,
      startPlace,
      endPlace,
      phone: undefined,
      confirmationCode: order.id,
      confirmationItemCodes: orderItemIds,
      confirmationUrl,
      price: Number(orderItem.total),
      url: offerUrl,
      code: orderOffer.id,
    }

    return payload
  }

  const payload: CreateTourRequestBody = {
    type: 'TOUR',
    name: offer.name,
    isBooked: true,
    sourceType: 'LE_Tour',
    startDate,
    endDate,
    startPlace,
    endPlace,
    phone: undefined,
    confirmationCode: order.id,
    confirmationItemCodes: orderItemIds,
    confirmationUrl,
    price: Number(orderItem.total),
    url: offerUrl,
    code: orderOffer.id,
  }

  return payload
}

const getCheckOutDate = ({
  checkInDate,
  checkOutDate,
  durationInDays,
}: BookmarkDatesAndOccupancy): moment.Moment | undefined => {
  if (checkOutDate) {
    return checkOutDate
  }
  if (checkInDate && durationInDays) {
    return checkInDate.add(durationInDays, 'days')
  }
  return undefined
}

export const getCheckInCheckOutOccupancy = (
  datesAndGuests: BookmarkDatesAndOccupancy,
): {
  checkIn?: string
  checkOut?: string
  occupancies?: Array<App.Occupants>
} => {
  const checkIn = datesAndGuests.checkInDate?.format(ISO_DATE_FORMAT)
  const checkOut = getCheckOutDate(datesAndGuests)?.format(ISO_DATE_FORMAT)
  return {
    checkIn,
    checkOut,
    occupancies: datesAndGuests.occupancies,
  }
}

export const getBedbankCheckInOutTime = (
  finePrint?: App.BedbankPropertyFinePrint,
) => {
  const checkInRaw = finePrint?.checkIn?.beginTime
  const checkInTime = checkInRaw && formatBedbankCheckInOutTime(checkInRaw)
  const checkOutRaw = finePrint?.checkOut?.time
  const checkOutTime = checkOutRaw && formatBedbankCheckInOutTime(checkOutRaw)
  return [checkInTime, checkOutTime]
}

async function fetchAirportLocation(
  code: string,
): Promise<App.AirportLocation | undefined> {
  try {
    const data = await getAirportsSearch(code)
    return data.find((d: App.AirportLocation) => d.airportCode === code)
  } catch (e) {
    return undefined
  }
}

export const getAirportLocation = memoize(fetchAirportLocation)
